import React, { useEffect, useState, useRef, useCallback } from 'react';
import Layout from '../../components/layout/Layout';
import { getData, postData, putData, deleteData, postDataWithFile, downloadCSV } from '../../services/apiService';
import {
    Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography,
    IconButton, Button, Modal, TextField, Select, MenuItem, Pagination, InputAdornment, InputLabel, FormControl, Alert, Tooltip
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from '../../contexts/AuthContext';
import Switch from '@mui/material/Switch';
import useDebounce from '../../hooks/useDebounce';
import Progress from '../../hooks/useProgressBar';
import useFetchIp from "../../hooks/useFetchIp";

const ErrorMessage = ({ error }) => (
    error ? <div className="text-danger small mt-1">{error}</div> : null
);

const AddStudent = () => {
    const [exportLoading, setExportLoading] = useState(false);
    const { ip } = useFetchIp();
    const [loading, setLoading] = useState(true);
    const { user } = useAuth();
    const [data, setData] = useState([]);
    const isFetched = useRef(false);
    const [formData, setFormData] = useState({
        firstName: '', lastName: '', email: '', phone: '', employee_id: '', adhaar: '',
        password: '', confirmPassword: '', affiliatedCollegeCode: '', affiliatedCollegeName: '', gender: ''
    });
    const [errors, setErrors] = useState({});
    const [showStudentModal, addStudentShowModal] = useState(false);
    const [updateStudentModalShow, updateStudentModal] = useState(false);
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchQuery, setSearchQuery] = useState('');
    const [totalPages, setTotalPages] = useState(1);
    const debouncedSearchQuery = useDebounce(searchQuery, 500);
    const [statusFilter, setStatusFilter] = useState('');


    const [instructionModalOpen, setInstructionModalOpen] = useState(false);
    const [excelImportStatus, setExcelImportStatus] = useState(null);
    const [progressFilter, setProgressFilter] = useState('');

    const currentYear = new Date().getFullYear();

    const [yearFilter, setYearFilter] = useState(currentYear.toString());
    const [monthFilter, setMonthFilter] = useState('');
    //     const [monthFilter, setMonthFilter] = useState('');
    // const [yearFilter, setYearFilter] = useState('');

    // --- Handler functions ---
    const handleInstructionOpen = () => setInstructionModalOpen(true);
    const handleInstructionClose = (event, reason) => {
        if (reason === "backdropClick") return;
        setInstructionModalOpen(false);
    };

    // --- Download function ---
    const handleDownloadFormat = () => {
        const link = document.createElement("a");
        link.href = "/sample_students_clg.xlsx";
        link.download = "sample_students_clg.xlsx";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Fetch students
    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const response = await getData(`/get-institution-student?page=${page}&limit=${rowsPerPage}&search=${debouncedSearchQuery}&isApproved=${statusFilter}&month=${monthFilter}&year=${yearFilter}&progressStatus=${progressFilter}`);
            if (response) {
                // Adjust according to your API's response shape
                setData(response.data || response); // fallback
                setTotalPages(response.totalPages || 1);
            }
        } catch (error) {
            console.error('Failed to fetch data', error);
        } finally {
            setLoading(false);
        }
    }, [page, rowsPerPage, debouncedSearchQuery, statusFilter, monthFilter, yearFilter, progressFilter]);

    useEffect(() => {
        if (!isFetched.current) {
            fetchData();
        }
    }, [fetchData]);

    // Search handlers
    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
        setPage(1);
    };

    const handlePageChange = (event, newPage) => {
        setPage(newPage);
    };

    const handleRowsPerPageChange = (event) => {
        setRowsPerPage(Number(event.target.value));
        setPage(1);
    };

    const handleInputChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    // Add new student (single)
    const addStudent = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const dataToSend = {
                ...formData,
                ipAddress: ip,
                college_id: user?.college_id
            };
            await postData('/add-student', dataToSend);
            // Reset form & close
            setFormData({ firstName: '', lastName: '', email: '', phone: '', employee_id: '', adhaar: '', password: '', confirmPassword: '', affiliatedCollegeCode: '', affiliatedCollegeName: '', gender: '' });
            setErrors({});
            addStudentShowModal(false);
            await fetchData();
        } catch (error) {
            console.error('Add student error:', error);
            // Validation errors from backend (express-validator style)
            if (error.response?.data?.errors) {
                const errorsObj = {};
                error.response.data.errors.forEach(err => {
                    errorsObj[err.path] = err.msg;
                });
                setErrors(errorsObj);
                setTimeout(() => setErrors({}), 5000);
            } else {
                alert(error.response?.data?.message || 'Failed to add student');
            }
        } finally {
            setLoading(false);
        }
    };

    // Edit: open modal with selected student
    const editStudent = (id) => {
        const studentData = data.find(item => item._id === id);
        if (!studentData) return;
        setSelectedStudent(studentData);
        updateStudentModal(true);
    };

    // Update student
    const updateStudent = async () => {
        if (!selectedStudent) return;
        setLoading(true);
        try {
            const updatedData = {
                firstName: selectedStudent.firstName,
                lastName: selectedStudent.lastName,
                email: selectedStudent.email,
                phone: selectedStudent.phone,
                adhaar: selectedStudent.adhaar,
                employee_id: selectedStudent.employee_id,
                affiliatedCollegeCode: selectedStudent.affiliatedCollegeCode,
                affiliatedCollegeName: selectedStudent.affiliatedCollegeName,
                gender: selectedStudent.gender,
                department_id: selectedStudent?.department_id?._id || selectedStudent.department_id,
                // isApproved: selectedStudent.isApproved,
                ipAddress: ip,
                college_id: user?.college_id
            };

            await putData(`/update-student/${selectedStudent._id}`, updatedData);
            await fetchData();
            updateStudentModal(false);
            setSelectedStudent(null);
            alert('Student updated successfully');
        } catch (error) {
            console.error('Failed to update student', error);
            if (error.response?.data?.errors) {
                const errorsObj = {};
                error.response.data.errors.forEach(err => {
                    errorsObj[err.path] = err.msg;
                });
                setErrors(errorsObj);
                setTimeout(() => setErrors({}), 5000);
            } else {
                alert(error.response?.data?.message || 'Failed to update student');
            }
        } finally {
            setLoading(false);
        }
    };

    // Change status (approve/unapprove)
    const handleStatusChange = async (id, newStatus) => {
        try {
            // optimistic update
            setData(data.map(item => item._id === id ? { ...item, isApproved: newStatus } : item));
            await putData(`/update-student/${id}`, { isApproved: newStatus, ipAddress: ip });
        } catch (error) {
            console.error('Failed to update status:', error);
            // rollback
            setData(data.map(item => item._id === id ? { ...item, isApproved: !newStatus } : item));
            alert('Failed to change status');
        }
    };

    // Delete student
    const deleteStudent = async (id) => {
        if (!window.confirm('Are you sure you want to delete this student?')) return;
        try {
            setLoading(true);
            await deleteData(`/delete-student-instructors/${id}/${ip}`);
            await fetchData();
            // alert('Student deleted');
        } catch (error) {
            console.error("Error deleting student:", error);
            // alert('Failed to delete student');
        } finally {
            setLoading(false);
        }
    };

    // Modals close handlers (prevent backdrop close if needed)
    const handleAddStudentModalClose = (event, reason) => {
        if (reason === "backdropClick") return;
        addStudentShowModal(false);
    };
    const handleUpdateStudentModalClose = (event, reason) => {
        if (reason === "backdropClick") return;
        updateStudentModal(false);
    };

    const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

    // Excel Upload Function (background job + polling — avoids gateway timeout on large files)
    const handleExcelUpload = async (e) => {
        const file = e.target.files[0];
        e.target.value = ''; // reset input
        if (!file) return;
        const excelForm = new FormData();
        excelForm.append("file", file);
        try {
            setExcelImportStatus('Uploading file…');
            const res = await postDataWithFile("upload-students", excelForm);
            const jobId = res?.jobId;
            if (!jobId) {
                setExcelImportStatus(null);
                await fetchData();
                alert(res?.message || 'Import acknowledged.');
                return;
            }
            setExcelImportStatus('Import running on server…');
            const maxAttempts = 7200;
            const intervalMs = 2000;
            for (let attempt = 0; attempt < maxAttempts; attempt++) {
                await sleep(intervalMs);
                const job = await getData(`upload-students/job/${jobId}`);
                if (job.chunksTotal > 0) {
                    setExcelImportStatus(
                        `Import in progress: chunk ${job.chunksProcessed || 0} of ${job.chunksTotal} (${job.totalRows || 0} rows)`
                    );
                }
                if (job.status === 'completed') {
                    setExcelImportStatus(null);
                    await fetchData();
                    alert(
                        `Import finished.\nSaved: ${job.totalSaved}\nDuplicates / skipped: ${job.totalDuplicates}\nRow errors: ${job.totalErrors}`
                    );
                    return;
                }
                if (job.status === 'failed') {
                    setExcelImportStatus(null);
                    alert(job.errorMessage || 'Import failed on the server.');
                    return;
                }
            }
            setExcelImportStatus(null);
            alert('Stopped checking import status after a long wait. Refresh the list later — the import may still be running.');
        } catch (error) {
            console.error("Error uploading file:", error);
            setExcelImportStatus(null);
            alert(error.response?.data?.message || 'Failed to import students');
        }
    };

    const clearFilters = () => {
        setStatusFilter('');
        setMonthFilter('');
        setYearFilter('');
        setProgressFilter('');
        setSearchQuery('');
        setPage(1);
    };


    const handleExportCSV = () => {
        if (!data || data.length === 0) {
            alert("No data to export");
            return;
        }

        const headers = [
            "Full Name",
            "College",
            "Email",
            "Student ID",
            "Month",
            "Year",
            "Status",
            "Progress Status",
            "Course Progress",
            "Assessment %"
        ];

        const rows = data.map(item => [
            `"${item.firstName} ${item.lastName}"`,
            `"${item.affiliatedCollegeName}"`,
            `"${item.email}"`,
            `"${item.employee_id}"`,
            `"${new Date(item.createdAt).toLocaleString('default', { month: 'long' })}"`,
            `"${new Date(item.createdAt).getFullYear()}"`,
            `"${item.isApproved ? "Approved" : "Unapproved"}"`,
            `"${item.progressStatus}"`,
            `"${item.progressPercentage}%"`,
            `"${item.quizPercentage}%"`
        ]);

        let csvContent =
            "data:text/csv;charset=utf-8," +
            [headers.map(h => `"${h}"`), ...rows]
                .map(e => e.join(","))
                .join("\n");

        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.href = encodedUri;
        link.download = "students_data.csv";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };


    // const handleExportAllCSV = async () => {
    //     try {
    //         const response = await getData( `/export-students-csv?isApproved=${statusFilter}` );

    //         const blob = await response.blob();
    //         const url = window.URL.createObjectURL(blob);

    //         const link = document.createElement("a");
    //         link.href = url;
    //         link.download = "students_all_data.csv";
    //         document.body.appendChild(link);
    //         link.click();
    //         document.body.removeChild(link);
    //     } catch (error) {
    //         console.error("Export failed", error);
    //         alert("Failed to export CSV");
    //     }
    // };

    // const handleExportAllCSV = async () => {
    //     try {
    //         // const response = await downloadCSV(
    //         //     `/export-students-csv?isApproved=${statusFilter}`
    //         // );
    //         const response = await downloadCSV(
    //             `/export-students-csv?search=${searchQuery}&isApproved=${statusFilter}&month=${monthFilter}&year=${yearFilter}`
    //         );

    //         const url = window.URL.createObjectURL(new Blob([response.data]));

    //         const link = document.createElement("a");
    //         link.href = url;
    //         link.download = "students_all_data.csv";

    //         document.body.appendChild(link);
    //         link.click();
    //         link.remove();

    //     } catch (error) {
    //         console.error(error);
    //         alert("Failed to export CSV");
    //     }
    // };


    const handleExportAllCSV = async () => {
        try {
            setExportLoading(true);

            const response = await downloadCSV(
                `/export-students-csv?search=${searchQuery}&isApproved=${statusFilter}&month=${monthFilter}&year=${yearFilter}&progressStatus=${progressFilter}`
            );

            const url = window.URL.createObjectURL(new Blob([response.data]));

            const link = document.createElement("a");
            link.href = url;
            link.download = "students_all_data.csv";

            document.body.appendChild(link);
            link.click();
            link.remove();

        } catch (error) {
            console.error(error);
            alert("Failed to export CSV");
        } finally {
            setExportLoading(false);
        }
    };

    return (
        <Layout>
            {exportLoading && <Progress />}
            {loading ? <Progress /> :
                <Box className="p-6 bg-gray-100 min-h-screen">
                    {excelImportStatus ? (
                        <Alert severity="info" sx={{ mb: 2 }}>{excelImportStatus}</Alert>
                    ) : null}
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mb: 3,
                            flexWrap: "wrap"
                        }}
                    >
                        <Typography variant="h5" component="h1" sx={{ fontWeight: 'bold' }}>
                            Students List
                        </Typography>

                        <Box
                            sx={{
                                display: "flex",
                                gap: 2,
                                alignItems: "center",
                                flexWrap: "wrap"
                            }}
                        >
                            {/* Add Student */}
                            {/* <Button
                                variant="contained"
                                onClick={() => addStudentShowModal(true)}
                                sx={{
                                    borderRadius: 2,
                                    fontWeight: 600,
                                    px: 3,
                                    py: 1,
                                    textTransform: 'none',
                                    backgroundColor: '#4B49AC',
                                    boxShadow: 2,
                                    height: 40,
                                    '&:hover': { backgroundColor: '#3A3897' }
                                }}
                            >
                                + Add Student
                            </Button> */}

                            {/* Import Excel */}
                            {/* <input
                                type="file"
                                accept=".xlsx,.xls"
                                style={{ display: "none" }}
                                id="upload-excel"
                                onChange={handleExcelUpload}
                            />
                            <label htmlFor="upload-excel">
                                <Button
                                    variant="outlined"
                                    component="span"
                                    startIcon={<UploadFileIcon />}
                                    sx={{
                                        borderRadius: 2,
                                        fontWeight: 600,
                                        textTransform: 'none',
                                        height: 40
                                    }}
                                >
                                    Import Excel
                                </Button>
                            </label> */}

                            {/* Instruction */}
                            {/* <Button
                                variant="outlined"
                                startIcon={<DownloadIcon />}
                                onClick={handleInstructionOpen}
                                sx={{
                                    borderRadius: 2,
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    color: 'green',
                                    borderColor: 'green',
                                    height: 40,
                                }}
                            >
                                Import Format
                            </Button> */}

                            {/* Export All CSV */}
                          
                            <Button
                                variant="contained"
                                startIcon={!exportLoading && <DownloadIcon />}
                                onClick={handleExportAllCSV}
                                disabled={exportLoading}
                                sx={{
                                    borderRadius: 2,
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    backgroundColor: '#FF9800',
                                    height: 40
                                }}
                            >
                                {exportLoading ? "Exporting..." : "Export CSV"}
                            </Button>
                        </Box>
                    </Box>

                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 3,
                            flexWrap: 'wrap',
                            gap: 2,
                        }}
                    >

                        {/* LEFT SIDE (Buttons + Filter) */}
                        <Box
                            sx={{
                                display: "flex",
                                gap: 2,
                                alignItems: 'center',
                                flexWrap: 'wrap'
                            }}
                        >
                            {/* Status Filter */}
                            <FormControl
                                size="small"
                                sx={{
                                    minWidth: 150,
                                    height: 40,
                                    justifyContent: "center",
                                    backgroundColor: "#fff",
                                    borderRadius: 2,
                                    boxShadow: "0px 0px 6px #00000014"
                                }}
                            >
                                <InputLabel>Status</InputLabel>
                                <Select
                                    value={statusFilter}
                                    label="Status"
                                    onChange={(e) => {
                                        setStatusFilter(e.target.value);
                                        setPage(1);
                                    }}
                                >
                                    <MenuItem value="">All</MenuItem>
                                    <MenuItem value="true">Approved</MenuItem>
                                    <MenuItem value="false">Unapproved</MenuItem>
                                </Select>
                            </FormControl>


                            <FormControl size="small"
                                sx={{
                                    minWidth: 150, height: 40, justifyContent: "center", backgroundColor: "#fff",
                                    borderRadius: 2, boxShadow: "0px 0px 6px #00000014"
                                }} >
                                <InputLabel>Year</InputLabel>
                                <Select value={yearFilter} label="year"
                                    // onChange={(e) => {
                                    //     setYearFilter(e.target.value);
                                    //     setPage(1);
                                    // }}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        setYearFilter(value);
                                        setPage(1);

                                        if (!value) {
                                            setMonthFilter(''); // ✅ reset month
                                        }
                                    }}
                                >
                                    <MenuItem value="">All</MenuItem>
                                    <MenuItem value="2023">2023</MenuItem>
                                    <MenuItem value="2024">2024</MenuItem>
                                    <MenuItem value="2025">2025</MenuItem>
                                    <MenuItem value="2026">2026</MenuItem>
                                </Select>
                            </FormControl>

                            <Tooltip title={!yearFilter ? "Please select the year first" : ""}>
                                <span>
                                    <FormControl
                                        size="small"
                                        disabled={!yearFilter}
                                        sx={{
                                            minWidth: 150,
                                            height: 40,
                                            justifyContent: "center",
                                            backgroundColor: "#fff",
                                            borderRadius: 2,
                                            boxShadow: "0px 0px 6px #00000014"
                                        }}
                                    >
                                        <InputLabel>Month</InputLabel>

                                        <Select
                                            value={monthFilter}
                                            label="Month"
                                            onChange={(e) => {
                                                setMonthFilter(e.target.value);
                                                setPage(1);
                                            }}
                                        >
                                            <MenuItem value="">All</MenuItem>
                                            <MenuItem value="1">Jan</MenuItem>
                                            <MenuItem value="2">Feb</MenuItem>
                                            <MenuItem value="3">Mar</MenuItem>
                                            <MenuItem value="4">Apr</MenuItem>
                                            <MenuItem value="5">May</MenuItem>
                                            <MenuItem value="6">Jun</MenuItem>
                                            <MenuItem value="7">Jul</MenuItem>
                                            <MenuItem value="8">Aug</MenuItem>
                                            <MenuItem value="9">Sep</MenuItem>
                                            <MenuItem value="10">Oct</MenuItem>
                                            <MenuItem value="11">Nov</MenuItem>
                                            <MenuItem value="12">Dec</MenuItem>
                                        </Select>
                                    </FormControl>
                                </span>
                            </Tooltip>

                            <FormControl size="small" sx={{ minWidth: 150, height: 40, backgroundColor: "#fff", borderRadius: 2, boxShadow: "0px 0px 6px #00000014" }} >
                                <InputLabel>Progress</InputLabel>
                                <Select value={progressFilter} label="Progress"
                                    onChange={(e) => {
                                        setProgressFilter(e.target.value);
                                        setPage(1);
                                    }}
                                >
                                    <MenuItem value="">All</MenuItem>
                                    <MenuItem value="Not Started">Not Started</MenuItem>
                                    <MenuItem value="In Progress">In Progress</MenuItem>
                                    <MenuItem value="Completed">Completed</MenuItem>
                                </Select>
                            </FormControl>

                            <Button
                                variant="contained"
                                onClick={() => clearFilters()}
                                sx={{
                                    borderRadius: 2, fontWeight: 600, px: 3,
                                    py: 1, textTransform: 'none', backgroundColor: '#c9c9d1',
                                    boxShadow: 2, height: 40, '&:hover': { backgroundColor: '#9393af' }
                                }}
                            >
                                × Clear
                            </Button>

                        </Box>

                        {/* RIGHT SIDE (Search) */}
                        <TextField placeholder="Search Students" value={searchQuery}
                            onChange={handleSearchChange} variant="outlined" size="small"
                            sx={{
                                width: { xs: "100%", sm: 185 },
                                bgcolor: '#f5f6fa',
                                borderRadius: 2,
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    height: 40
                                },
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: '#757575' }} />
                                    </InputAdornment>
                                ),
                            }}
                        />

                    </Box>


                    {/* Table */}
                    {/* <TableContainer
                        component={Paper}
                        sx={{
                            width: "100%",
                            overflowX: { xs: "auto", md: "hidden" },
                            "&::-webkit-scrollbar": {
                                height: "8px"
                            },
                            "&::-webkit-scrollbar-thumb": {
                                backgroundColor: "#888",
                                borderRadius: "4px"
                            }
                        }}
                    > */}

                    <TableContainer
                        component={Paper}
                        sx={{
                            width: "100%",
                            maxWidth: "100%",
                            overflowX: "auto",
                            overflowY: "hidden",
                            "&::-webkit-scrollbar": {
                                height: "8px"
                            },
                            "&::-webkit-scrollbar-thumb": {
                                backgroundColor: "#888",
                                borderRadius: "4px"
                            }
                        }}
                    >

                        {/* <TableContainer component={Paper} sx={{ overflowX: "auto", width: "100%" }}> */}
                        {/* <TableContainer component={Paper} elevation={0} sx={{ mb: 2, borderRadius: 2, boxShadow: 2, overflow: 'hidden', }} > */}
                        {/* <Table> */}
                        <Table sx={{ minWidth: 1200 }}>
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#4B49AC' }}>
                                    <TableCell align="center" sx={{ color: 'white', fontWeight: 600 }}> ID </TableCell>
                                    <TableCell align="center" sx={{ color: 'white', fontWeight: 600 }}> Full Name  </TableCell>
                                    <TableCell align="center" sx={{ color: 'white', fontWeight: 600 }}> Affiliated College </TableCell>
                                    <TableCell align="center" sx={{ color: 'white', fontWeight: 600 }}> Email </TableCell>
                                    {/* <TableCell align="center" sx={{ color: 'white', fontWeight: 600 }}> Phone </TableCell> */}
                                    <TableCell align="center" sx={{ color: 'white', fontWeight: 600 }}> Students Id </TableCell>
                                    <TableCell align="center" sx={{ color: 'white', fontWeight: 600 }}> Enrollment Month </TableCell>
                                    <TableCell align="center" sx={{ color: 'white', fontWeight: 600 }}> Enrollment Year </TableCell>
                                    <TableCell align="center" sx={{ color: 'white', fontWeight: 600 }}> Status </TableCell>
                                    <TableCell align="center" sx={{ color: 'white', fontWeight: 600 }}> Progress Status </TableCell>
                                    <TableCell align="center" sx={{ color: 'white', fontWeight: 600 }}> Course Progress </TableCell>
                                    <TableCell align="center" sx={{ color: 'white', fontWeight: 600 }}> Assessment Percentage </TableCell>
                                    {/* <TableCell align="center" sx={{ color: 'white', fontWeight: 600 }}> Action </TableCell> */}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {data && data.map((item, index) => (
                                    <TableRow key={item._id || index}>
                                        <TableCell align="center">
                                            <Typography variant="body1">{(page - 1) * rowsPerPage + index + 1}</Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Typography variant="body1">{item.firstName} {item.lastName}</Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Typography variant="body1">{item.affiliatedCollegeName}</Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Typography variant="body1">{item.email}</Typography>
                                        </TableCell>
                                        {/* <TableCell align="center">
                                            <Typography variant="body1">{item.phone}</Typography>
                                        </TableCell> */}
                                        <TableCell align="center">
                                            <Typography variant="body1">{item.employee_id}</Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Typography variant="body1">
                                                {new Date(item.createdAt).toLocaleString('default', { month: 'long' })}
                                            </Typography>
                                        </TableCell>

                                        <TableCell align="center">
                                            <Typography variant="body1">
                                                {new Date(item.createdAt).getFullYear()}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Switch
                                                checked={item.isApproved || false}
                                                onChange={() => handleStatusChange(item._id, !item.isApproved)}
                                                sx={{
                                                    '& .MuiSwitch-switchBase.Mui-checked': {
                                                        color: '#4caf50',
                                                    },
                                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                        backgroundColor: '#4caf50',
                                                    },
                                                    '& .MuiSwitch-switchBase': {
                                                        color: '#f44336',
                                                    },
                                                    '& .MuiSwitch-track': {
                                                        backgroundColor: '#f44336',
                                                    },
                                                }}
                                            />
                                        </TableCell>

                                        <TableCell align="center">
                                            <Typography
                                                variant="body1"
                                                sx={{
                                                    color:
                                                        item.progressStatus === "Completed"
                                                            ? "green"
                                                            : item.progressStatus === "In Progress"
                                                                ? "orange"
                                                                : "red",
                                                    fontWeight: 600
                                                }}
                                            >
                                                {item.progressStatus}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Typography variant="body1">{item.progressPercentage}%</Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Typography variant="body1">{item.quizPercentage}%</Typography>
                                        </TableCell>
                                        {/* <TableCell align="center">
                                            <IconButton color="primary" onClick={() => editStudent(item._id)}>
                                                <EditIcon />
                                            </IconButton>
                                            <IconButton color="error" onClick={() => deleteStudent(item._id)}>
                                                <DeleteIcon />
                                            </IconButton>
                                        </TableCell> */}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {/* Pagination + rows per page */}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2 }}>
                        <Pagination count={totalPages} page={page} onChange={handlePageChange} shape="rounded"
                            sx={{
                                '& .MuiPaginationItem-root': {
                                    '&.Mui-selected': {
                                        bgcolor: '#4B49AC',
                                        color: 'white',
                                    },
                                },
                            }}
                        />
                        <Select value={rowsPerPage} onChange={handleRowsPerPageChange} size="small"
                            sx={{ ml: 2, height: 35, bgcolor: '#f7f7f7', border: '1px solid #e2e2e2', '& .MuiSelect-select': { py: 0.75 }, }}
                            IconComponent={KeyboardArrowDownIcon} >
                            <MenuItem value={10}>10 / page</MenuItem>
                            <MenuItem value={20}>20 / page</MenuItem>
                            <MenuItem value={50}>50 / page</MenuItem>
                        </Select>
                    </Box>

                    {/* Add Student Modal */}
                    <Modal open={showStudentModal} onClose={handleAddStudentModalClose} aria-labelledby="add-student-modal">
                        <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 900, bgcolor: 'background.paper', boxShadow: 24, p: 4, borderRadius: 2, }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="h6" component="h2">Add Student</Typography>
                                <IconButton onClick={() => addStudentShowModal(false)}
                                    sx={{ position: 'absolute', right: 8, top: 8, color: 'gray' }} >
                                    <CloseIcon />
                                </IconButton>
                            </Box>
                            <form onSubmit={addStudent}>
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                                        <TextField fullWidth label={<>First Name <span style={{ color: 'red' }}>*</span> </>} name="firstName" value={formData.firstName} onChange={handleInputChange} margin="normal" sx={{ flex: 1 }} />
                                        <ErrorMessage error={errors.firstName} />
                                    </Box>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                                        <TextField fullWidth label="Last Name" name="lastName" value={formData.lastName} onChange={handleInputChange} margin="normal" sx={{ flex: 1 }} />
                                        <ErrorMessage error={errors.lastName} />
                                    </Box>
                                </Box>

                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                                        <TextField fullWidth label={<>Email <span style={{ color: 'red' }}>*</span> </>} name="email" value={formData.email} onChange={handleInputChange} margin="normal" sx={{ flex: 1 }} />
                                        <ErrorMessage error={errors.email} />
                                    </Box>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                                        <TextField fullWidth label={<>Phone <span style={{ color: 'red' }}>*</span> </>} name="phone" value={formData.phone} onChange={handleInputChange} margin="normal" sx={{ flex: 1 }} />
                                        <ErrorMessage error={errors.phone} />
                                    </Box>
                                </Box>

                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                                        <TextField fullWidth label={<>Employee Id <span style={{ color: 'red' }}>*</span> </>} name="employee_id" value={formData.employee_id} onChange={handleInputChange} margin="normal" sx={{ flex: 1 }} />
                                        <ErrorMessage error={errors.employee_id} />
                                    </Box>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                                        <TextField fullWidth label={<>Adhaar <span style={{ color: 'red' }}>*</span> </>} name="adhaar" value={formData.adhaar} onChange={handleInputChange} margin="normal" sx={{ flex: 1 }} />
                                        <ErrorMessage error={errors.adhaar} />
                                    </Box>
                                </Box>

                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                                        <TextField fullWidth label='Affiliated College Name' name="affiliatedCollegeName" value={formData.affiliatedCollegeName} onChange={handleInputChange} margin="normal" sx={{ flex: 1 }} />
                                        <ErrorMessage error={errors.affiliatedCollegeName} />
                                    </Box>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                                        <TextField fullWidth label='Affiliated College Code' name="affiliatedCollegeCode" value={formData.affiliatedCollegeCode} onChange={handleInputChange} margin="normal" sx={{ flex: 1 }} />
                                        <ErrorMessage error={errors.affiliatedCollegeCode} />
                                    </Box>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                                        <FormControl fullWidth margin="normal">
                                            <InputLabel>Gender<span style={{ color: 'red' }}>*</span></InputLabel>
                                            <Select name="gender" value={formData.gender} onChange={handleInputChange} label="Gender" >
                                                <MenuItem value="male">Male</MenuItem>
                                                <MenuItem value="female">Female</MenuItem>
                                                <MenuItem value="other">Other</MenuItem>
                                            </Select>
                                        </FormControl>
                                        <ErrorMessage error={errors.gender} />
                                    </Box>
                                </Box>

                                <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                                        <TextField fullWidth type="password" label={<>Password <span style={{ color: 'red' }}>*</span> </>} name="password" value={formData.password} onChange={handleInputChange} margin="normal" sx={{ flex: 1 }} />
                                        <ErrorMessage error={errors.password} />
                                    </Box>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                                        <TextField fullWidth type="password" label={<> Confirm Password <span style={{ color: 'red' }}>*</span> </>} name="confirmPassword" value={formData.confirmPassword} onChange={handleInputChange} margin="normal" sx={{ flex: 1 }} />
                                    </Box>
                                </Box>

                                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                                    <Button variant="contained" sx={{
                                        borderRadius: 2, fontWeight: 600, boxShadow: 2, px: 3, py: 1, textTransform: 'none', backgroundColor: '#4B49AC',
                                        '&:hover': { backgroundColor: '#3A3897', },
                                    }} type="submit"> Save
                                    </Button>
                                </Box>
                            </form>
                        </Box>
                    </Modal>

                    {/* Update Student Modal */}
                    <Modal open={updateStudentModalShow} onClose={handleUpdateStudentModalClose} aria-labelledby="update-student-modal" >
                        <Box sx={{
                            position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 900,
                            bgcolor: 'background.paper', boxShadow: 24, p: 4, borderRadius: 2,
                        }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="h6" component="h2">Update Student</Typography>
                                <IconButton onClick={() => updateStudentModal(false)} sx={{ position: 'absolute', right: 8, top: 8, color: 'gray' }} >
                                    <CloseIcon />
                                </IconButton>
                            </Box>
                            <form>
                                <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                                        <TextField fullWidth label="First Name" name="firstName" value={selectedStudent?.firstName || ""}
                                            onChange={(e) => setSelectedStudent({ ...selectedStudent, firstName: e.target.value })}
                                            margin="normal" sx={{ flex: 1 }} />
                                        <ErrorMessage error={errors.firstName} />
                                    </Box>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                                        <TextField fullWidth label="Last Name" name="lastName" value={selectedStudent?.lastName || ""}
                                            onChange={(e) => setSelectedStudent({ ...selectedStudent, lastName: e.target.value })}
                                            margin="normal" sx={{ flex: 1 }} />
                                        <ErrorMessage error={errors.lastName} />
                                    </Box>
                                </Box>

                                <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                                        <TextField fullWidth label="Email" name="email" value={selectedStudent?.email || ""}
                                            onChange={(e) => setSelectedStudent({ ...selectedStudent, email: e.target.value })}
                                            margin="normal" sx={{ flex: 1 }} />
                                        <ErrorMessage error={errors.email} />
                                    </Box>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                                        <TextField fullWidth label="Phone" name="phone" value={selectedStudent?.phone || ""}
                                            onChange={(e) => setSelectedStudent({ ...selectedStudent, phone: e.target.value })}
                                            margin="normal" sx={{ flex: 1 }} />
                                        <ErrorMessage error={errors.phone} />
                                    </Box>
                                </Box>

                                <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                                        <TextField fullWidth label="Adhaar Number" name="adhaar" value={selectedStudent?.adhaar || ""}
                                            onChange={(e) => setSelectedStudent({ ...selectedStudent, adhaar: e.target.value })}
                                            margin="normal" sx={{ flex: 1 }} />
                                        <ErrorMessage error={errors.adhaar} />
                                    </Box>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                                        <TextField fullWidth label="Enrollment Number (Username)" name="employee_id" value={selectedStudent?.employee_id || ""}
                                            onChange={(e) => setSelectedStudent({ ...selectedStudent, employee_id: e.target.value })}
                                            margin="normal" sx={{ flex: 1 }} InputProps={{ readOnly: true }} />
                                        <ErrorMessage error={errors.employee_id} />
                                    </Box>
                                </Box>

                                <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                                        <TextField fullWidth label="Affiliated College Name" name="affiliatedCollegeName" value={selectedStudent?.affiliatedCollegeName || ""}
                                            onChange={(e) => setSelectedStudent({ ...selectedStudent, affiliatedCollegeName: e.target.value })}
                                            margin="normal" sx={{ flex: 1 }} />
                                        <ErrorMessage error={errors.affiliatedCollegeName} />
                                    </Box>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                                        <TextField fullWidth label="Affiliated College Code" name="affiliatedCollegeCode" value={selectedStudent?.affiliatedCollegeCode || ""}
                                            onChange={(e) => setSelectedStudent({ ...selectedStudent, affiliatedCollegeCode: e.target.value })}
                                            margin="normal" sx={{ flex: 1 }} />
                                        <ErrorMessage error={errors.affiliatedCollegeCode} />
                                    </Box>
                                </Box>

                                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                                    <Button variant="contained" sx={{
                                        borderRadius: 2, fontWeight: 600, boxShadow: 2, px: 3, py: 1, textTransform: 'none', backgroundColor: '#4B49AC', '&:hover': { backgroundColor: '#3A3897', },
                                    }} onClick={updateStudent}>
                                        Save changes
                                    </Button>
                                </Box>
                            </form>
                        </Box>
                    </Modal>

                    {/* Instruction Formats Modal */}
                    <Modal open={instructionModalOpen} onClose={handleInstructionClose}>
                        <Box
                            sx={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                width: 600,
                                bgcolor: 'background.paper',
                                boxShadow: 24,
                                p: 4,
                                borderRadius: 2,
                            }}
                        >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="h6" component="h2">Instruction Format</Typography>
                                <IconButton onClick={handleInstructionClose} sx={{ color: 'gray' }}>
                                    <CloseIcon />
                                </IconButton>
                            </Box>

                            <Typography variant="body1" sx={{ mb: 2 }}>
                                Your Excel file must include the following columns:
                            </Typography>

                            <TableContainer component={Paper} elevation={0} sx={{ mb: 2 }}>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell><b>Column Name</b></TableCell>
                                            <TableCell><b>Description</b></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        <TableRow><TableCell>firstName</TableCell><TableCell>Student First Name</TableCell></TableRow>
                                        <TableRow><TableCell>lastName</TableCell><TableCell>Student Last Name</TableCell></TableRow>
                                        <TableRow><TableCell>gender</TableCell><TableCell>Student Gender</TableCell></TableRow>
                                        <TableRow><TableCell>email</TableCell><TableCell>Email Address</TableCell></TableRow>
                                        <TableRow><TableCell>phone</TableCell><TableCell>Mobile Number</TableCell></TableRow>
                                        <TableRow><TableCell>adhaar</TableCell><TableCell>Adhaar Number</TableCell></TableRow>
                                        <TableRow><TableCell>password</TableCell><TableCell>Login Password</TableCell></TableRow>
                                        <TableRow><TableCell>confirmPassword</TableCell><TableCell>Confirm Password</TableCell></TableRow>
                                        <TableRow><TableCell>employee_id</TableCell><TableCell>Enrollment Number</TableCell></TableRow>
                                        <TableRow><TableCell>department_id</TableCell><TableCell>Department ID</TableCell></TableRow>
                                        <TableRow><TableCell>isApproved</TableCell><TableCell>Status (true/false)</TableCell></TableRow>
                                        <TableRow><TableCell>ipAddress</TableCell><TableCell>System IP Address</TableCell></TableRow>
                                        <TableRow><TableCell>affiliatedCollegeCode</TableCell><TableCell>Affiliated College Code</TableCell></TableRow>
                                        <TableRow><TableCell>affiliatedCollegeName</TableCell><TableCell>Affiliated College Name</TableCell></TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <Button
                                    variant="contained"
                                    startIcon={<DownloadIcon />}
                                    onClick={handleDownloadFormat}
                                    sx={{
                                        borderRadius: 2,
                                        fontWeight: 600,
                                        backgroundColor: 'green',
                                        '&:hover': { backgroundColor: 'darkgreen' },
                                    }}
                                >
                                    Download Sample Excel
                                </Button>
                            </Box>
                        </Box>
                    </Modal>


                </Box>
            }
        </Layout>
    );
};

export default AddStudent;
